import { Box, Button, Heading, HStack, Text, VStack } from "@chakra-ui/react";
import { LuTriangleAlert, LuArrowLeft, LuFileX, LuSearchX } from "react-icons/lu";
import { Logo } from "@/components/logo";

// ── Standalone pages (root-level errors, rendered without Provider/Navbar) ──
// These must include Provider externally when used in createRootRoute.

export function StandaloneNotFound() {
  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" px="6">
      <VStack gap="8" textAlign="center" maxW="sm">
        <Logo size="lg" />
        <VStack gap="4">
          <Box color="fg.muted">
            <LuSearchX size={48} />
          </Box>
          <Heading as="h1" size="2xl">
            Page not found
          </Heading>
          <Text color="fg.muted">The page you're looking for doesn't exist or has been moved.</Text>
        </VStack>
        <Button asChild colorPalette="loonie">
          <a href="/dashboard">Go to Dashboard</a>
        </Button>
      </VStack>
    </Box>
  );
}

interface StandaloneErrorProps {
  error: Error;
  reset: () => void;
}

export function StandaloneError({ error, reset }: StandaloneErrorProps) {
  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" px="6">
      <VStack gap="8" textAlign="center" maxW="sm">
        <Logo size="lg" />
        <VStack gap="4">
          <Box color="red.500">
            <LuTriangleAlert size={48} />
          </Box>
          <Heading as="h1" size="2xl">
            Something went wrong
          </Heading>
          <Text color="fg.muted">
            An unexpected error occurred. Please try again or return to the dashboard.
          </Text>
          {error.message && (
            <Box
              bg="bg.subtle"
              borderWidth="1px"
              borderColor="border"
              borderRadius="md"
              px="4"
              py="3"
              w="full"
              textAlign="left"
            >
              <Text fontSize="sm" fontFamily="mono" color="fg.muted" wordBreak="break-word">
                {error.message}
              </Text>
            </Box>
          )}
        </VStack>
        <HStack gap="3">
          <Button variant="outline" onClick={reset}>
            Try again
          </Button>
          <Button asChild colorPalette="loonie">
            <a href="/dashboard">Go to Dashboard</a>
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}

// ── Inline content (route-level, Navbar/Provider available from parent routes) ──

interface NotFoundContentProps {
  title: string;
  description: string;
  backHref: string;
  backLabel: string;
}

export function NotFoundContent({ title, description, backHref, backLabel }: NotFoundContentProps) {
  return (
    <VStack align="center" gap="6" py="16" textAlign="center">
      <Box color="fg.muted">
        <LuFileX size={48} />
      </Box>
      <VStack gap="2">
        <Heading as="h2" size="xl">
          {title}
        </Heading>
        <Text color="fg.muted" maxW="sm">
          {description}
        </Text>
      </VStack>
      <Button asChild variant="outline">
        <a href={backHref}>
          <LuArrowLeft size={16} />
          {backLabel}
        </a>
      </Button>
    </VStack>
  );
}

interface ErrorContentProps {
  error: Error;
  reset: () => void;
}

export function ErrorContent({ error, reset }: ErrorContentProps) {
  return (
    <VStack align="center" gap="6" py="16" textAlign="center">
      <Box color="red.500">
        <LuTriangleAlert size={48} />
      </Box>
      <VStack gap="2">
        <Heading as="h2" size="xl">
          Something went wrong
        </Heading>
        <Text color="fg.muted" maxW="sm">
          An unexpected error occurred. Please try again.
        </Text>
      </VStack>
      {error.message && (
        <Box
          bg="bg.subtle"
          borderWidth="1px"
          borderColor="border"
          borderRadius="md"
          px="4"
          py="3"
          maxW="sm"
          w="full"
          textAlign="left"
        >
          <Text fontSize="sm" fontFamily="mono" color="fg.muted" wordBreak="break-word">
            {error.message}
          </Text>
        </Box>
      )}
      <Button variant="outline" onClick={reset}>
        Try again
      </Button>
    </VStack>
  );
}
